<template>
  <div class="popover-wrapper">
    <div ref="popoverRef" :id="`${popover.name}-popover`" class="popover">
      <slot name="content" />
      <!-- <div class="popover__arrow">
        <DropdownIcon color="black" />
      </div> -->
    </div>

    <div class="popover-backdrop"></div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import type { PopoverStoreType } from '@/features/ui/store/popover/popover.store'
import popoversStore from '@/features/ui/store/popover/popover.store'
import useWindowSize from '@/hooks/use-window-size.hook'
import { BREAKPOINTS } from '@/utils/config/constants'

/* -------------------------------------------------- */
/* Props et état local                                */
/* -------------------------------------------------- */
const props = defineProps<{ popover: PopoverStoreType }>()
const emit = defineEmits(['emit:close'])

const isOpenRef = computed(() => props.popover.isOpen ?? false)
const popoverRef = ref<HTMLDivElement | null>(null)

const { widthRef } = useWindowSize()

/* -------------------------------------------------- */
/* Fonctions utilitaires                              */
/* -------------------------------------------------- */
const POSITION_MARGIN = 32
const OPEN_CLASS = 'popover--open'
let fadeOutTimeout: ReturnType<typeof setTimeout> | null = null
let hasMouseLeaveListener = false

function repositionPopover() {
  const popover = popoverRef.value
  if (!popover) return

  // Sur mobile : position gérée par le CSS
  if (widthRef.value < BREAKPOINTS.DESKTOP || !props.popover.event) {
    popover.style.left = ''
    popover.style.top = ''
    return
  }

  /* ---------- Desktop ---------- */
  const { clientX: xClick, clientY: yClick } = props.popover.event
  const rect = popover.getBoundingClientRect()
  const windowHeight = window.innerHeight

  /* Axe X */
  if (xClick < rect.width - POSITION_MARGIN) {
    popover.style.left = `${xClick}px`
  } else {
    popover.style.left = `${xClick - rect.width}px`
  }

  /* Axe Y */
  const y = yClick - 8
  if (y < rect.height) {
    popover.style.top = `${y}px`
  } else if (y > windowHeight - rect.height - POSITION_MARGIN) {
    popover.style.top = `${y - rect.height}px`
  } else {
    popover.style.top = `${y}px`
  }
}

function openPopover() {
  const popover = popoverRef.value
  if (!popover) return

  /* Annule un éventuel fade-out en cours */
  if (fadeOutTimeout) {
    clearTimeout(fadeOutTimeout)
    fadeOutTimeout = null
  }

  props.popover.event?.stopPropagation()
  popover.classList.add(OPEN_CLASS)

  // On attend le DOM pour une mesure exacte
  nextTick(repositionPopover)
}

function closePopover(immediate = false) {
  const popover = popoverRef.value
  if (!popover) return

  popover.classList.remove(OPEN_CLASS)

  const commitClose = () => {
    popover.style.left = '-1000px'
    popover.style.top = '-1000px'
    popoversStore.togglePopover(props.popover.name, false)
    emit('emit:close')
  }

  if (immediate) {
    commitClose()
  } else {
    /* Laisse l’animation CSS se terminer (240 ms) */
    fadeOutTimeout = setTimeout(commitClose, 240)
  }
}

/* -------------------------------------------------- */
/* Listeners globaux                                  */
/* -------------------------------------------------- */
function handleMouseLeave() {
  closePopover()
}

function handleMissClick(e: MouseEvent) {
  const popover = popoverRef.value
  if (!popover || !isOpenRef.value) return

  const path = (e.composedPath ? e.composedPath() : []) as Array<unknown>
  const clickedInside = path.includes(popover) || popover.contains(e.target as Node)

  if (!clickedInside) {
    closePopover(true) // Sans délai : click extérieur
  }
}

/* -------------------------------------------------- */
/* Watchers                                           */
/* -------------------------------------------------- */
watch(isOpenRef, (open) => {
  if (open) openPopover()
  else closePopover()
})

watch(widthRef, () => {
  if (isOpenRef.value) repositionPopover()

  if (widthRef.value >= BREAKPOINTS.DESKTOP && !hasMouseLeaveListener) {
    popoverRef.value?.addEventListener('mouseleave', handleMouseLeave)
    hasMouseLeaveListener = true
  } else if (widthRef.value < BREAKPOINTS.DESKTOP && hasMouseLeaveListener) {
    popoverRef.value?.removeEventListener('mouseleave', handleMouseLeave)
    hasMouseLeaveListener = false
  }
})

/* -------------------------------------------------- */
/* Cycle de vie                                       */
/* -------------------------------------------------- */
onMounted(() => {
  document.addEventListener('click', handleMissClick)
  if (widthRef.value >= BREAKPOINTS.DESKTOP) {
    popoverRef.value?.addEventListener('mouseleave', handleMouseLeave)
    hasMouseLeaveListener = true
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleMissClick)
  if (hasMouseLeaveListener) {
    popoverRef.value?.removeEventListener('mouseleave', handleMouseLeave)
  }
  if (fadeOutTimeout) clearTimeout(fadeOutTimeout)
})
</script>

<style lang="scss">
@import './popover.scss';
</style>
